import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';
import moment from 'moment';
import { getOperationalMetrics } from '../services/api';
import { useSnackbar } from '../contexts/SnackbarContext';

const REFRESH_INTERVAL_MS = 15000;
const pct = (value) => `${Number(value || 0).toFixed(2)}%`;

const toIsoDateKey = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value.slice(0, 10);
    return moment.utc(value).format('YYYY-MM-DD');
};

const aggregateByPeriod = (rows, periodKey) => {
    const grouped = new Map();

    rows.forEach((row) => {
        const period = row[periodKey];
        if (!period) return;

        const existing = grouped.get(period) || {
            period,
            ordersIn: 0,
            ordersOut: 0,
            dispatchedOrders: 0,
            returnedOrders: 0,
        };

        existing.ordersIn += Number(row.ordersIn || 0);
        existing.ordersOut += Number(row.ordersOut || 0);
        existing.dispatchedOrders += Number(row.dispatchedOrders || 0);
        existing.returnedOrders += Number(row.returnedOrders || 0);
        grouped.set(period, existing);
    });

    return Array.from(grouped.values()).sort((a, b) => new Date(a.period) - new Date(b.period));
};

const getLatestAvailableDatePair = (rows, preferredDate) => {
    const preferred = moment(preferredDate, 'YYYY-MM-DD', true).isValid()
        ? preferredDate
        : moment().format('YYYY-MM-DD');
    const availableDates = Array.from(
        new Set((rows || []).map((row) => row.periodDate).filter(Boolean))
    ).sort();

    if (availableDates.length === 0) {
        return {
            currentDate: preferred,
            previousDate: moment(preferred).subtract(1, 'day').format('YYYY-MM-DD'),
            usedFallback: false,
        };
    }

    const currentDate = [...availableDates].reverse().find((date) => date <= preferred)
        || availableDates[availableDates.length - 1];
    const previousDate = [...availableDates].reverse().find((date) => date < currentDate)
        || moment(currentDate).subtract(1, 'day').format('YYYY-MM-DD');

    return {
        currentDate,
        previousDate,
        usedFallback: currentDate !== preferred,
    };
};

const buildDispatchByClientChartData = (rows, targetDate, sortMode) => {
    const grouped = new Map();

    rows.forEach((row) => {
        if (row.periodDate !== targetDate) return;
        const current = grouped.get(row.clientName) || {
            clientName: row.clientName,
            expressEligibleOrders: 0,
            expressOnTimeOrders: 0,
            standardEligibleOrders: 0,
            standardOnTimeOrders: 0,
        };

        current.expressEligibleOrders += row.expressEligibleOrders;
        current.expressOnTimeOrders += row.expressOnTimeOrders;
        current.standardEligibleOrders += row.standardEligibleOrders;
        current.standardOnTimeOrders += row.standardOnTimeOrders;
        grouped.set(row.clientName, current);
    });

    return Array.from(grouped.values())
        .filter((item) => (item.expressEligibleOrders + item.standardEligibleOrders) > 0)
        .map((item) => ({
            clientName: item.clientName,
            expressOnTimePct: item.expressEligibleOrders === 0
                ? 100
                : Number(((item.expressOnTimeOrders * 100) / item.expressEligibleOrders).toFixed(2)),
            standardOnTimePct: item.standardEligibleOrders === 0
                ? 100
                : Number(((item.standardOnTimeOrders * 100) / item.standardEligibleOrders).toFixed(2)),
        }))
        .sort((a, b) => {
            const metricA = Math.min(a.standardOnTimePct, a.expressOnTimePct);
            const metricB = Math.min(b.standardOnTimePct, b.expressOnTimePct);
            if (metricA === metricB) return a.clientName.localeCompare(b.clientName);
            return sortMode === 'HIGHEST' ? metricB - metricA : metricA - metricB;
        });
};

const buildSnapshotFilters = () => {
    const today = moment().format('YYYY-MM-DD');
    return {
        startDate: today,
        endDate: today,
        clientName: 'ALL',
        workingDayStart: '07:00',
        workingDayEnd: '17:00',
        shippingPriority: 'ALL',
        includeOnHoldOrders: false,
        standardSlaDays: 2,
    };
};

const buildSnapshotComparisonFilters = () => {
    const today = moment().format('YYYY-MM-DD');
    const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD');
    return {
        startDate: yesterday,
        endDate: today,
        clientName: 'ALL',
        workingDayStart: '07:00',
        workingDayEnd: '17:00',
        shippingPriority: 'ALL',
        includeOnHoldOrders: false,
        standardSlaDays: 2,
    };
};

const OperationalReportingSnapshot = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [dispatchByClientSort, setDispatchByClientSort] = useState('HIGHEST');
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));
    const [metrics, setMetrics] = useState({
        dispatchOnTime: { daily: [], dailyByClient: [], monthly: [] },
        throughput: { daily: [], weekly: [], monthly: [] },
        returnRate: { weekly: [], monthly: [] },
    });
    const [dispatchByClientRowsSource, setDispatchByClientRowsSource] = useState([]);

    const requestIdRef = useRef(0);

    const fetchSnapshot = useCallback(
        async ({ initial = false } = {}) => {
            const requestId = ++requestIdRef.current;
            if (initial) {
                setLoading(true);
            } else {
                setIsRefreshing(true);
            }

            try {
                const [todayResponse, comparisonResponse] = await Promise.all([
                    getOperationalMetrics(buildSnapshotFilters()),
                    getOperationalMetrics(buildSnapshotComparisonFilters()),
                ]);
                if (requestId !== requestIdRef.current) return;

                let loadedAny = false;

                if (todayResponse?.success) {
                    setMetrics({
                        dispatchOnTime: todayResponse.dispatchOnTime || { daily: [], dailyByClient: [], monthly: [] },
                        throughput: todayResponse.throughput || { daily: [], weekly: [], monthly: [] },
                        returnRate: todayResponse.returnRate || { weekly: [], monthly: [] },
                    });
                    loadedAny = true;
                }

                if (comparisonResponse?.success) {
                    setDispatchByClientRowsSource(comparisonResponse.dispatchOnTime?.dailyByClient || []);
                    loadedAny = true;
                } else if (todayResponse?.success) {
                    // Fallback to today's rows if comparison fetch fails.
                    setDispatchByClientRowsSource(todayResponse.dispatchOnTime?.dailyByClient || []);
                }

                if (loadedAny) {
                    setLastUpdated(new Date());
                } else {
                    showSnackbar('Failed to load operational snapshot.', 'error');
                }
            } catch (error) {
                if (requestId !== requestIdRef.current) return;
                const backendMessage = error?.response?.data?.message;
                showSnackbar(backendMessage || 'Failed to load operational snapshot.', 'error');
                console.error('Error loading operational snapshot:', error);
            } finally {
                if (requestId === requestIdRef.current) {
                    if (initial) setLoading(false);
                    setIsRefreshing(false);
                }
            }
        },
        [showSnackbar]
    );

    useEffect(() => {
        fetchSnapshot({ initial: true });
    }, [fetchSnapshot]);

    useEffect(() => {
        const timerId = setInterval(() => {
            fetchSnapshot();
        }, REFRESH_INTERVAL_MS);

        return () => clearInterval(timerId);
    }, [fetchSnapshot]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(Boolean(document.fullscreenElement));
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    const handleToggleFullscreen = async () => {
        try {
            if (!document.fullscreenElement) {
                await document.documentElement.requestFullscreen();
            } else {
                await document.exitFullscreen();
            }
        } catch (error) {
            showSnackbar('Unable to toggle fullscreen mode.', 'warning');
        }
    };

    const throughputDaily = useMemo(
        () => aggregateByPeriod(metrics.throughput.daily || [], 'periodDate'),
        [metrics.throughput.daily]
    );

    const selectedRangeOrdersIn = useMemo(
        () => throughputDaily.reduce((sum, row) => sum + Number(row.ordersIn || 0), 0),
        [throughputDaily]
    );

    const selectedRangeOrdersOut = useMemo(
        () => throughputDaily.reduce((sum, row) => sum + Number(row.ordersOut || 0), 0),
        [throughputDaily]
    );

    const selectedRangeDispatchSummary = useMemo(() => {
        const rows = metrics.dispatchOnTime.daily || [];
        const totals = rows.reduce(
            (acc, row) => ({
                expressEligibleOrders: acc.expressEligibleOrders + Number(row.expressEligibleOrders || 0),
                expressOnTimeOrders: acc.expressOnTimeOrders + Number(row.expressOnTimeOrders || 0),
                standardEligibleOrders: acc.standardEligibleOrders + Number(row.standardEligibleOrders || 0),
                standardOnTimeOrders: acc.standardOnTimeOrders + Number(row.standardOnTimeOrders || 0),
            }),
            {
                expressEligibleOrders: 0,
                expressOnTimeOrders: 0,
                standardEligibleOrders: 0,
                standardOnTimeOrders: 0,
            }
        );

        const expressOnTimePct = totals.expressEligibleOrders === 0
            ? 100
            : Number(((totals.expressOnTimeOrders * 100) / totals.expressEligibleOrders).toFixed(2));

        const standardOnTimePct = totals.standardEligibleOrders === 0
            ? 100
            : Number(((totals.standardOnTimeOrders * 100) / totals.standardEligibleOrders).toFixed(2));

        return {
            ...totals,
            expressOnTimePct,
            standardOnTimePct,
        };
    }, [metrics.dispatchOnTime.daily]);

    const selectedRangeReturnPct = useMemo(() => {
        const weeklyRows = metrics.returnRate.weekly || [];
        const monthlyRows = metrics.returnRate.monthly || [];
        const rows = weeklyRows.length ? weeklyRows : monthlyRows;
        const totals = rows.reduce(
            (acc, row) => ({
                dispatchedOrders: acc.dispatchedOrders + Number(row.dispatchedOrders || 0),
                returnedOrders: acc.returnedOrders + Number(row.returnedOrders || 0),
            }),
            { dispatchedOrders: 0, returnedOrders: 0 }
        );
        return totals.dispatchedOrders === 0
            ? 0
            : Number(((totals.returnedOrders * 100) / totals.dispatchedOrders).toFixed(2));
    }, [metrics.returnRate.weekly, metrics.returnRate.monthly]);

    const dispatchDailyByClientRows = useMemo(
        () =>
            (dispatchByClientRowsSource || []).map((row) => ({
                periodDate: toIsoDateKey(row.periodDate),
                clientName: String(row.clientName || '').trim() || 'Unknown Client',
                expressEligibleOrders: Number(row.expressEligibleOrders || 0),
                expressOnTimeOrders: Number(row.expressOnTimeOrders || 0),
                standardEligibleOrders: Number(row.standardEligibleOrders || 0),
                standardOnTimeOrders: Number(row.standardOnTimeOrders || 0),
            })),
        [dispatchByClientRowsSource]
    );

    const preferredDispatchTodayDate = moment().format('YYYY-MM-DD');
    const dispatchDatePair = useMemo(
        () => getLatestAvailableDatePair(dispatchDailyByClientRows, preferredDispatchTodayDate),
        [dispatchDailyByClientRows, preferredDispatchTodayDate]
    );
    const dispatchClientTodayDate = dispatchDatePair.currentDate;
    const dispatchClientYesterdayDate = dispatchDatePair.previousDate;

    const dispatchByClientTodayChart = useMemo(
        () => buildDispatchByClientChartData(dispatchDailyByClientRows, dispatchClientTodayDate, dispatchByClientSort),
        [dispatchDailyByClientRows, dispatchClientTodayDate, dispatchByClientSort]
    );

    const dispatchByClientYesterdayChart = useMemo(
        () => buildDispatchByClientChartData(dispatchDailyByClientRows, dispatchClientYesterdayDate, dispatchByClientSort),
        [dispatchDailyByClientRows, dispatchClientYesterdayDate, dispatchByClientSort]
    );

    const dispatchTodayLabel = moment(dispatchClientTodayDate, 'YYYY-MM-DD').format('DD MMM');
    const dispatchYesterdayLabel = moment(dispatchClientYesterdayDate, 'YYYY-MM-DD').format('DD MMM');
    const dispatchCurrentDayHeading = dispatchDatePair.usedFallback ? 'Latest Data Day' : 'Today';
    const calendarPreviousDate = moment(dispatchClientTodayDate, 'YYYY-MM-DD').subtract(1, 'day').format('YYYY-MM-DD');
    const dispatchPreviousDayHeading = dispatchClientYesterdayDate === calendarPreviousDate
        ? 'Yesterday'
        : 'Last Working Day';
    const dispatchByClientTodayHeight = Math.min(Math.max(dispatchByClientTodayChart.length * 36 + 80, 260), 560);
    const dispatchByClientYesterdayHeight = Math.min(Math.max(dispatchByClientYesterdayChart.length * 36 + 80, 260), 560);

    return (
        <Box sx={{ p: { xs: 1, sm: 2 }, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        Operations Reporting TV Snapshot
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        All clients, all priorities. Cards are today; chart compares today vs yesterday. Auto-refresh every 15 seconds.
                        {lastUpdated ? ` Last updated: ${moment(lastUpdated).format('HH:mm:ss')}` : ''}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate('/operational-reporting')}
                    >
                        Back to Operations Reporting
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={() => fetchSnapshot()}
                        disabled={isRefreshing}
                    >
                        {isRefreshing ? 'Refreshing...' : 'Refresh Now'}
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        onClick={handleToggleFullscreen}
                    >
                        {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                    </Button>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(220px, 1fr))' },
                            gap: 2,
                            alignItems: 'stretch',
                            mb: 2,
                        }}
                    >
                        <Box sx={{ minWidth: 0, display: 'flex' }}>
                            <Paper elevation={1} sx={{ p: 2, borderLeft: 4, borderColor: 'info.main', borderRadius: 1, flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <ArrowDownwardIcon color="info" fontSize="small" />
                                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                        Total Orders In (selected range)
                                    </Typography>
                                </Box>
                                <Typography variant="h5" fontWeight={700}>{selectedRangeOrdersIn.toLocaleString()}</Typography>
                            </Paper>
                        </Box>
                        <Box sx={{ minWidth: 0, display: 'flex' }}>
                            <Paper elevation={1} sx={{ p: 2, borderLeft: 4, borderColor: 'warning.main', borderRadius: 1, flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <ArrowUpwardIcon color="warning" fontSize="small" />
                                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                        Total Orders Out (selected range)
                                    </Typography>
                                </Box>
                                <Typography variant="h5" fontWeight={700}>{selectedRangeOrdersOut.toLocaleString()}</Typography>
                            </Paper>
                        </Box>
                        <Box sx={{ minWidth: 0, display: 'flex' }}>
                            <Paper elevation={1} sx={{ p: 2, borderLeft: 4, borderColor: 'primary.main', borderRadius: 1, flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <CheckCircleOutlineIcon color="primary" fontSize="small" />
                                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                        Express On-Time (All Priorities, selected range)
                                    </Typography>
                                </Box>
                                <Typography variant="h5" fontWeight={700}>{pct(selectedRangeDispatchSummary.expressOnTimePct)}</Typography>
                            </Paper>
                        </Box>
                        <Box sx={{ minWidth: 0, display: 'flex' }}>
                            <Paper elevation={1} sx={{ p: 2, borderLeft: 4, borderColor: 'success.main', borderRadius: 1, flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                        Standard SLA Hit (All Priorities, selected range)
                                    </Typography>
                                </Box>
                                <Typography variant="h5" fontWeight={700}>{pct(selectedRangeDispatchSummary.standardOnTimePct)}</Typography>
                            </Paper>
                        </Box>
                        <Box sx={{ minWidth: 0, display: 'flex' }}>
                            <Paper elevation={1} sx={{ p: 2, borderLeft: 4, borderColor: 'secondary.main', borderRadius: 1, flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <AssessmentIcon color="secondary" fontSize="small" />
                                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                        Return Rate (All Priorities, selected range)
                                    </Typography>
                                </Box>
                                <Typography variant="h5" fontWeight={700}>{pct(selectedRangeReturnPct)}</Typography>
                            </Paper>
                        </Box>
                    </Box>

                    <Paper elevation={3} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="h6">
                                Dispatch On Time by Client - Today vs Yesterday (All Priorities)
                            </Typography>
                            <ToggleButtonGroup
                                value={dispatchByClientSort}
                                exclusive
                                onChange={(event, value) => value && setDispatchByClientSort(value)}
                                size="small"
                            >
                                <ToggleButton value="LOWEST">Lowest SLA First</ToggleButton>
                                <ToggleButton value="HIGHEST">Highest SLA First</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            Y-axis: client name, X-axis: on-time percentage.
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                gap: 2,
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {dispatchCurrentDayHeading} ({dispatchTodayLabel})
                                </Typography>
                                {dispatchByClientTodayChart.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No client dispatch on-time data available for today.
                                    </Typography>
                                ) : (
                                    <Box sx={{ width: '100%', height: dispatchByClientTodayHeight, minWidth: 0 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={dispatchByClientTodayChart} layout="vertical" margin={{ left: 12, right: 16 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis type="number" domain={[0, 100]} />
                                            <YAxis type="category" dataKey="clientName" width={150} />
                                            <Tooltip formatter={(value) => pct(value)} />
                                            <Legend />
                                            <Bar dataKey="standardOnTimePct" fill="#2e7d32" name="Standard SLA %" />
                                            <Bar dataKey="expressOnTimePct" fill="#1976d2" name="Express On-Time %" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {dispatchPreviousDayHeading} ({dispatchYesterdayLabel})
                                </Typography>
                                {dispatchByClientYesterdayChart.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No client dispatch on-time data available for yesterday.
                                    </Typography>
                                ) : (
                                    <Box sx={{ width: '100%', height: dispatchByClientYesterdayHeight, minWidth: 0 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={dispatchByClientYesterdayChart} layout="vertical" margin={{ left: 12, right: 16 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis type="number" domain={[0, 100]} />
                                            <YAxis type="category" dataKey="clientName" width={150} />
                                            <Tooltip formatter={(value) => pct(value)} />
                                            <Legend />
                                            <Bar dataKey="standardOnTimePct" fill="#2e7d32" name="Standard SLA %" />
                                            <Bar dataKey="expressOnTimePct" fill="#1976d2" name="Express On-Time %" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default OperationalReportingSnapshot;
